/**
 * Утилиты для работы с переводами
 */

/**
 * Получить перевод по ключу с поддержкой namespace
 * @param {Object} resources - Ресурсы переводов
 * @param {string} key - Ключ перевода (может содержать namespace)
 * @param {Object} options - Опции (interpolation, count, etc.)
 * @returns {string} - Переведенный текст
 */
export const getTranslation = (resources, key, options = {}) => {
  if (!key) return '';
  
  // Поддержка namespace в формате "namespace:key"
  const parts = key.split(':');
  let namespace = 'common';
  let translationKey = key;
  
  if (parts.length > 1) {
    namespace = parts[0];
    translationKey = parts.slice(1).join(':');
  }
  
  const namespaceResource = resources[namespace];
  if (!namespaceResource) {
    console.warn(`Translation namespace "${namespace}" not found`);
    return key;
  }
  
  // Получить перевод по вложенным ключам
  const keys = translationKey.split('.');
  let translation = namespaceResource;
  
  for (const k of keys) {
    if (translation && typeof translation === 'object' && k in translation) {
      translation = translation[k];
    } else {
      console.warn(`Translation key "${key}" not found in namespace "${namespace}"`);
      return key;
    }
  }
  
  if (typeof translation !== 'string') {
    console.warn(`Translation for key "${key}" is not a string`);
    return key;
  }
  
  // Интерполяция переменных
  if (options.interpolation) {
    translation = interpolate(translation, options.interpolation);
  }
  
  // Плюрализация
  if (options.count !== undefined) {
    translation = pluralize(translation, options.count, options.lng);
  }
  
  return translation;
};

/**
 * Интерполяция переменных в строке
 * @param {string} text - Текст с переменными
 * @param {Object} values - Значения для подстановки
 * @returns {string} - Текст с подставленными значениями
 */
const interpolate = (text, values) => {
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return values[key] !== undefined ? values[key] : match;
  });
};

/**
 * Плюрализация для русского языка
 * @param {string} text - Текст
 * @param {number} count - Количество
 * @param {string} lng - Язык
 * @returns {string} - Плюрализованный текст
 */
const pluralize = (text, count, lng = 'ru') => {
  if (lng === 'ru') {
    // Простая плюрализация для русского языка
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return text.replace(/\|(\d+)\|(\d+)\|(\d+)/, '$3');
    } else if (lastDigit === 1) {
      return text.replace(/\|(\d+)\|(\d+)\|(\d+)/, '$1');
    } else if (lastDigit >= 2 && lastDigit <= 4) {
      return text.replace(/\|(\d+)\|(\d+)\|(\d+)/, '$2');
    } else {
      return text.replace(/\|(\d+)\|(\d+)\|(\d+)/, '$3');
    }
  }
  
  return text;
};

/**
 * Проверить наличие перевода
 * @param {Object} resources - Ресурсы переводов
 * @param {string} key - Ключ перевода
 * @returns {boolean} - Есть ли перевод
 */
export const hasTranslation = (resources, key) => {
  try {
    const translation = getTranslation(resources, key);
    return translation !== key;
  } catch {
    return false;
  }
};

/**
 * Получить все ключи переводов из namespace
 * @param {Object} resources - Ресурсы переводов
 * @param {string} namespace - Namespace
 * @returns {Array} - Массив ключей
 */
export const getTranslationKeys = (resources, namespace = 'common') => {
  const namespaceResource = resources[namespace];
  if (!namespaceResource) return [];
  
  const keys = [];
  const extractKeys = (obj, prefix = '') => {
    for (const key in obj) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        extractKeys(obj[key], fullKey);
      } else {
        keys.push(fullKey);
      }
    }
  };
  
  extractKeys(namespaceResource);
  return keys;
};

/**
 * Валидация переводов
 * Проверяет базовую структуру и содержимое ресурсов переводов
 * 
 * Примечание: эта функция отличается от runTranslationValidation из validateTranslations.js:
 * 1. Проверяет только один набор ресурсов, а не сравнивает языки между собой
 * 2. Выполняет базовую проверку (тип данных, пустые строки)
 * 3. Предназначена для программного использования в приложении
 * 
 * @param {Object} resources - Ресурсы переводов
 * @returns {Object} - Результат валидации
 */
export const validateTranslations = (resources) => {
  const errors = [];
  const warnings = [];
  
  for (const namespace in resources) {
    const namespaceResource = resources[namespace];
    if (typeof namespaceResource !== 'object') {
      errors.push(`Namespace "${namespace}" is not an object`);
      continue;
    }
    
    const validateObject = (obj, path = '') => {
      for (const key in obj) {
        const fullPath = path ? `${path}.${key}` : key;
        const value = obj[key];
        
        if (typeof value === 'object' && value !== null) {
          validateObject(value, fullPath);
        } else if (typeof value !== 'string') {
          errors.push(`Translation at "${namespace}:${fullPath}" is not a string`);
        } else if (value.trim() === '') {
          warnings.push(`Translation at "${namespace}:${fullPath}" is empty`);
        }
      }
    };
    
    validateObject(namespaceResource);
  }
  
  return { errors, warnings };
}; 